<template>
  <!-- 頁面標題 -->
  <div class="page-header">
    <h1>庫存管理</h1>
    <div class="header-actions">
      <button
        class="btn-batch"
        :disabled="selectedIds.length === 0"
        @click="openBatchAdjustModal"
      >
        批量調整庫存
      </button>
      <button class="btn-export" @click="showAdjustmentRecordsModal = true">
        庫存調整紀錄
      </button>
      <button class="btn-export" @click="exportReport">匯出庫存報表</button>
      <button class="btn-export" @click="importInventory">匯入盤點報表</button>
      <button class="btn-export" @click="exportInventory">匯出盤點報表</button>
    </div>
  </div>

  <!-- 搜尋 -->
  <div class="search-filter">
    <div class="search-box">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="搜尋商品或規格..."
        @input="handleSearch"
      />
    </div>
  </div>

  <!-- 手機/平板卡片版全選區塊 -->
  <div class="mobile-selection-bar" v-if="filteredInventory.length > 0">
    <label class="select-all-label">
      <span>全選目前頁面</span>
      <input type="checkbox" v-model="isAllSelected" />
    </label>
    <span class="selected-count">已選取 {{ selectedIds.length }} 項</span>
  </div>

  <!-- 手機/平板卡片版 -->
  <div class="inventory-cards">
    <div v-for="item in pagedInventory" :key="item.id" class="inventory-card">
      <div class="card-header">
        <div class="card-header-left">
          <input
            type="checkbox"
            :value="item.id"
            v-model="selectedIds"
            class="item-checkbox"
          />
          <h3 class="card-title">{{ item.productName }}</h3>
        </div>
        <span :class="['status-tag', 'status-' + item.status]">
          {{ getStatusText(item.status) }}
        </span>
      </div>

      <div class="card-body">
        <div class="card-row">
          <span class="card-label">規格</span>
          <span class="card-value">{{ item.variantName || "-" }}</span>
        </div>

        <div class="card-row">
          <span class="card-label">庫存量</span>
          <span class="card-value">{{ item.stock }}</span>
        </div>

        <div class="card-row">
          <span class="card-label">安全庫存</span>
          <span class="card-value">{{ item.safetyStock }}</span>
        </div>

        <!-- ✅ 新增入庫日期 -->
        <div class="card-row">
          <span class="card-label">入庫日期</span>
          <span class="card-value">{{ formatDate(item.updatedDate) }}</span>
        </div>
      </div>

      <div class="card-footer">
        <button class="action-btn stock" @click="openAdjustModal(item)">
          調整庫存
        </button>
      </div>
    </div>
  </div>

  <!-- 電腦版表格 -->
  <div class="inventory-table">
    <table>
      <thead>
        <tr>
          <th style="width: 40px">
            <input type="checkbox" v-model="isAllSelected" />
          </th>
          <th>商品名稱</th>
          <th>規格</th>
          <th>庫存量</th>
          <th>安全庫存</th>
          <th>入庫日期</th>
          <th>狀態</th>
          <th>操作</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="item in pagedInventory" :key="item.id">
          <td>
            <input type="checkbox" :value="item.id" v-model="selectedIds" />
          </td>
          <td>{{ item.productName }}</td>
          <td>{{ item.variantName || "-" }}</td>
          <td>{{ item.stock }}</td>
          <td>{{ item.safetyStock }}</td>
          <td>{{ formatDate(item.updatedDate) }}</td>
          <td>
            <span :class="['status-tag', 'status-' + item.status]">
              {{ getStatusText(item.status) }}
            </span>
          </td>

          <td>
            <div class="action-buttons">
              <button class="action-btn stock" @click="openAdjustModal(item)">
                調整庫存
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- 分頁器 -->
  <div class="pagination" v-if="filteredInventory.length > 0">
    <button @click="prevPage" :disabled="currentPage === 1">上一頁</button>
    <span>第 {{ currentPage }} / {{ totalPages }} 頁</span>
    <button @click="nextPage" :disabled="currentPage === totalPages">
      下一頁
    </button>
  </div>

  <!-- 空狀態 -->
  <div class="empty-state" v-if="filteredInventory.length === 0">
    <div class="empty-icon">📦</div>
    <p>目前沒有庫存資料</p>
  </div>

  <!-- 調整庫存 Modal -->
  <div class="modal-overlay" v-if="showAdjustModal">
    <div class="modal">
      <div class="modal-header">
        <h2>調整庫存</h2>
        <button class="close-btn" @click="closeAdjustModal">×</button>
      </div>

      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">商品名稱</label>
          <div class="info-text">
            {{ adjustItem.productName }} {{ adjustItem.variantName }}
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">庫存量 *</label>
          <input
            type="number"
            class="form-input"
            v-model.number="adjustItem.stock"
            min="0"
          />
        </div>

        <div class="form-group">
          <label class="form-label">安全庫存 *</label>
          <input
            type="number"
            class="form-input"
            v-model.number="adjustItem.safetyStock"
            min="0"
          />
        </div>

        <!-- =============================== -->
        <!-- 備註輸入區域（非必填） -->
        <!-- =============================== -->
        <div class="form-group">
          <label class="form-label">備註</label>
          <textarea
            class="form-input"
            v-model="adjustItem.remark"
            placeholder="可輸入調整原因，例如：盤點補貨"
            rows="3"
          ></textarea>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="closeAdjustModal">取消</button>
        <button class="btn-submit" @click="saveStock">儲存</button>
      </div>
    </div>
  </div>

  <!-- 批量調整庫存 Modal -->
  <div class="modal-overlay" v-if="showBatchModal">
    <div class="modal">
      <div class="modal-header">
        <h2>批量調整庫存</h2>
        <button class="close-btn" @click="closeBatchAdjustModal">×</button>
      </div>

      <div class="modal-body">
        <p class="batch-count">已選取 {{ selectedIds.length }} 項商品</p>

        <div class="form-group">
          <label class="form-label">庫存量 (設定為相同值)</label>
          <input
            type="number"
            class="form-input"
            v-model.number="batchAdjustForm.stock"
            placeholder="留空則不修改"
            min="0"
          />
        </div>

        <div class="form-group">
          <label class="form-label">安全庫存 (設定為相同值)</label>
          <input
            type="number"
            class="form-input"
            v-model.number="batchAdjustForm.safetyStock"
            placeholder="留空則不修改"
            min="0"
          />
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="closeBatchAdjustModal">取消</button>
        <button class="btn-submit" @click="saveBatchStock">儲存</button>
      </div>
    </div>
  </div>

  <!-- 庫存調整紀錄 Modal -->
  <div class="modal-overlay" v-if="showAdjustmentRecordsModal">
    <div class="modal modal-lg">
      <div class="modal-header">
        <h2>庫存調整紀錄</h2>
        <button class="close-btn" @click="showAdjustmentRecordsModal = false">
          ×
        </button>
      </div>

      <div class="modal-body">
        <!-- 搜尋/篩選區塊 -->
        <div class="record-filters">
          <div class="filter-group">
            <label class="filter-label">開始日期</label>
            <input
              type="date"
              v-model="recordFilters.startDate"
              class="filter-input"
              :max="todayDate"
            />
          </div>

          <div class="filter-group">
            <label class="filter-label">結束日期</label>
            <input
              type="date"
              v-model="recordFilters.endDate"
              class="filter-input"
              :class="{ 'input-error': recordDateError }"
              :max="todayDate"
            />
            <div v-if="recordDateError" class="error-message">
              {{ recordDateError }}
            </div>
          </div>

          <div class="filter-group">
            <label class="filter-label">操作人員</label>
            <select v-model="recordFilters.createdBy" class="filter-input">
              <option value="">-- 全部 --</option>
              <option
                v-for="employee in employees"
                :key="employee"
                :value="employee"
              >
                {{ employee }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <button class="btn-reset" @click="resetRecordFilters">
              重設篩選
            </button>
            <button
              class="btn-refresh"
              @click="refreshAdjustmentRecords"
              :disabled="loading"
            >
              {{ loading ? "載入中..." : "刷新資料" }}
            </button>
          </div>
        </div>

        <!-- 手機版卡片 -->
        <div class="record-cards" v-if="pagedAdjustmentRecords.length > 0">
          <div
            v-for="(record, index) in pagedAdjustmentRecords"
            :key="index"
            class="record-card"
          >
            <div class="card-row">
              <span class="card-label">商品</span>
              <span class="card-value">{{ record.productName }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">規格</span>
              <span class="card-value">{{ record.variantName || "-" }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">異動類型</span>
              <span :class="['card-value', 'change-type-' + record.changeType]">
                {{ formatChangeType(record.changeType) }}
              </span>
            </div>
            <div class="card-row">
              <span class="card-label">異動數量</span>
              <span
                :class="[
                  'card-value',
                  record.changeQty >= 0 ? 'text-green' : 'text-red',
                ]"
              >
                {{ record.changeQty >= 0 ? "+" : "" }}{{ record.changeQty }}
              </span>
            </div>
            <div class="card-row">
              <span class="card-label">異動前庫存</span>
              <span class="card-value">{{ record.beforeStock }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">異動後庫存</span>
              <span class="card-value">{{ record.afterStock }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">備註</span>
              <span class="card-value">{{ record.remark || "-" }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">操作人員</span>
              <span class="card-value">{{ record.createdBy }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">操作時間</span>
              <span class="card-value">{{
                formatDate(record.createdDate)
              }}</span>
            </div>
          </div>
        </div>

        <!-- 電腦版表格 -->
        <div class="record-table" v-if="pagedAdjustmentRecords.length > 0">
          <table>
            <thead>
              <tr>
                <th>商品名稱</th>
                <th>規格</th>
                <th>異動類型</th>
                <th>異動數量</th>
                <th>調整前庫存</th>
                <th>調整後庫存</th>
                <th>備註</th>
                <th>操作人員</th>
                <th>操作時間</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(record, index) in pagedAdjustmentRecords"
                :key="index"
              >
                <td>{{ record.productName }}</td>
                <td>{{ record.variantName || "-" }}</td>
                <td>
                  <span :class="'change-type-' + record.changeType">
                    {{ formatChangeType(record.changeType) }}
                  </span>
                </td>
                <td :class="record.changeQty >= 0 ? 'text-green' : 'text-red'">
                  {{ record.changeQty >= 0 ? "+" : "" }}{{ record.changeQty }}
                </td>
                <td>{{ record.beforeStock }}</td>
                <td>{{ record.afterStock }}</td>
                <td>{{ record.remark || "-" }}</td>
                <td>{{ record.createdBy }}</td>
                <td>{{ formatDate(record.createdDate) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分頁器 -->
        <div class="pagination" v-if="filteredAdjustmentRecords.length > 0">
          <button @click="recordPrevPage" :disabled="recordCurrentPage === 1">
            上一頁
          </button>
          <span>第 {{ recordCurrentPage }} / {{ recordTotalPages }} 頁</span>
          <button
            @click="recordNextPage"
            :disabled="recordCurrentPage === recordTotalPages"
          >
            下一頁
          </button>
        </div>

        <!-- 空狀態 -->
        <div class="empty-state" v-if="filteredAdjustmentRecords.length === 0">
          <div class="empty-icon">📋</div>
          <p>目前沒有調整紀錄</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script src="@/assets/js/sale.js"></script>
<style scoped src="@/assets/css/sale.css"></style>
